import '@progress/kendo-theme-bootstrap/dist/all.css';

import React, { useContext, useEffect, useState } from 'react';

import { State, toDataSourceRequestString } from '@progress/kendo-data-query';
import { SelectDescriptor } from '@progress/kendo-react-data-tools';
import {
  Grid,
  GridColumn as Column,
  GridDataStateChangeEvent,
  GridHeaderSelectionChangeEvent,
  GridSelectionChangeEvent,
  GridSortChangeEvent,
} from '@progress/kendo-react-grid';

import { DistributionGridSelectionContext } from '../context/DistributionGridSelectionContext';
import DistributionGridSkeleton from './DistributionGridSkeleton';
import { useGetAuditCount, useGetAudits } from '../hooks/AuditHooks';
import { RequestTypeEnum } from '../models/api/Enums';
import { PlanSponsorAuditSummary } from '../models/api/PlanSponsorAuditSummary';
import { getApiUrl } from '../utils/query';

interface DistributionItem {
  id: number;
  selected: boolean;
  status: string;
  controlName: string;
  planName: string;
  requestId: string;
  coveragePeriod: string;
  sbcDocumentExternalId?: string;
  downloadType: string;
}

const initialDataState: State = {
  take: 5,
  skip: 0,
  filter: { logic: 'and', filters: [] },
  sort: [{ field: 'id', dir: 'asc' }],
};

const DistributionGrid: React.FC = () => {
  const { setSelectedAuditIds } = useContext(DistributionGridSelectionContext)!;

  const [dataState, setDataState] = useState<State>(initialDataState);
  const [select, setSelect] = useState<SelectDescriptor>({});

  const [isDownloading, setIsDownloading] = useState(false);
  const [activeDownload, setActiveDownload] = useState<'sbc' | 'subscriberEmail' | null>(
    null
  );

  const params = new URLSearchParams(window.location.search);
  const token = params.get('token') ?? '';
  const aud = params.get('aud') ?? '';

  // Fetch audit data. isLoading (not isPending) so a link without token/aud, whose
  // queries are disabled, doesn't sit on the skeleton forever.
  const { data: audits = [], isLoading: getAuditLoading } = useGetAudits(
    toDataSourceRequestString(dataState),
    token,
    aud
  );
  const { data: auditCount = 0, isLoading: getAuditCountLoading } = useGetAuditCount(
    toDataSourceRequestString(dataState),
    token,
    aud
  );

  const distributionData: DistributionItem[] = audits.map(
    (audit: PlanSponsorAuditSummary) => {
      const distributed = audit.subscriberDistributionDate
        ? new Date(audit.subscriberDistributionDate)
        : null;

      const status = distributed
        ? `Distributed on ${distributed.toLocaleDateString('en-US')}`
        : 'Distribution date needed';

      // Determine policy period start date based on request type
      const isExistingBusiness = audit.typeId === RequestTypeEnum.Existing;
      const startDateValue = isExistingBusiness
        ? audit.renewalDate
        : audit.proposedNewBusinessEffectiveDate;
      const startDate = startDateValue
        ? new Date(startDateValue).toLocaleDateString('en-US')
        : '';
      const endDate = audit.policyPeriodEndDate
        ? new Date(audit.policyPeriodEndDate).toLocaleDateString('en-US')
        : '';
      const coveragePeriod = startDate && endDate ? `${startDate} - ${endDate}` : '';

      return {
        id: audit.id,
        selected: false,
        status,
        controlName: audit.customerNameWillAppearOnSBC ?? '',
        planName: audit.productNameWillAppearInTheSBC ?? '',
        requestId: audit.requestId.toString(),
        coveragePeriod,
        sbcDocumentExternalId: audit.sbcDocumentExternalId,
        downloadType: 'SBC',
      };
    }
  );

  const selectedRows = React.useMemo(() => {
    const selectedKeys = Object.entries(select ?? {})
      .filter(([, value]) => Boolean(value))
      .map(([key]) => key);

    if (selectedKeys.length === 0) {
      return [];
    }

    const keySet = new Set(selectedKeys);
    return distributionData.filter((item) => keySet.has(String(item.id)));
  }, [distributionData, select]);

  const selectedSbcExternalIds = React.useMemo(
    () =>
      selectedRows
        .map((item) => item.sbcDocumentExternalId?.trim())
        .filter((value): value is string => Boolean(value)),
    [selectedRows]
  );

  const selectedAuditIdsForEmail = React.useMemo(
    () => selectedRows.map((item) => item.id),
    [selectedRows]
  );

  const isLoading = getAuditLoading || getAuditCountLoading;
  const sbcDownloadDisabled =
    isLoading || isDownloading || selectedSbcExternalIds.length === 0;
  const subscriberEmailDownloadDisabled =
    isLoading || isDownloading || selectedAuditIdsForEmail.length === 0;
  const handleDataStateChange = (event: GridDataStateChangeEvent) =>
    setDataState(event.dataState);
  const handleSortChange = (event: GridSortChangeEvent) =>
    setDataState({ ...dataState, sort: event.sort });

  const onSelectionChange = React.useCallback((event: GridSelectionChangeEvent) => {
    setSelect(event.select);
  }, []);

  const onHeaderSelectionChange = React.useCallback(
    (event: GridHeaderSelectionChangeEvent) => {
      setSelect(event.select);
    },
    []
  );

  useEffect(() => {
    // Update context with selected audit IDs whenever selection changes
    setSelectedAuditIds(selectedRows.map((item) => item.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [select]);

  // Custom cell components

  const DownloadSbcCell = (props) => {
    const item: DistributionItem = props.dataItem;
    const extId = item.sbcDocumentExternalId;
    // Build against API origin only (strip any trailing /api from base URL)
    const apiBase = getApiUrl();
    const href = extId ? new URL(`/document/${extId}`, apiBase).href : undefined;
    const disabled = !href;
    return (
      <td>
        {disabled ? (
          <span style={{ color: '#999', fontSize: '14px', padding: '4px 8px' }}>
            Download SBC
          </span>
        ) : (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: '#0066cc',
              textDecoration: 'underline',
              fontSize: '14px',
              padding: '4px 8px',
              display: 'inline-block',
            }}
          >
            Download SBC
          </a>
        )}
      </td>
    );
  };

  const DownloadSubscriberEmailCell = (props) => {
    const item: DistributionItem = props.dataItem;
    const auditId = item.id;
    const extId = item.sbcDocumentExternalId;
    const apiBase = getApiUrl();
    const href =
      auditId && extId
        ? new URL(`/document/subscriber-email/${auditId}`, apiBase).href
        : undefined;
    const disabled = !href;

    return (
      <td>
        {disabled ? (
          <span
            style={{
              color: '#999',
              fontSize: '14px',
              padding: '4px 8px',
              display: 'inline-block',
            }}
          >
            Download Subscriber Email
          </span>
        ) : (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: '#0066cc',
              textDecoration: 'underline',
              fontSize: '14px',
              padding: '4px 8px',
              display: 'inline-block',
            }}
          >
            Download Subscriber Email
          </a>
        )}
      </td>
    );
  };
  const downloadSelectedDocuments = React.useCallback(
    async ({
      externalIds,
      fileName,
      errorContext,
      downloadType,
    }: {
      externalIds: string[];
      fileName: string;
      errorContext: string;
      downloadType: 'sbc' | 'subscriberEmail';
    }) => {
      if (isDownloading || externalIds.length === 0) {
        return;
      }

      const apiBase = getApiUrl();
      const endpoint = new URL('/document/zip', apiBase).toString();

      try {
        setIsDownloading(true);
        setActiveDownload(downloadType);
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ externalIds }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || `Failed to download selected ${errorContext}.`);
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error(`Failed to download selected ${errorContext}`, error);
        window.alert(
          `Unable to download the selected ${errorContext}. Please try again.`
        );
      } finally {
        setIsDownloading(false);
        setActiveDownload(null);
      }
    },
    [isDownloading]
  );

  const handleDownloadSelectedSBCs = React.useCallback(
    () =>
      downloadSelectedDocuments({
        externalIds: selectedSbcExternalIds,
        fileName: 'Selected-SBCs.zip',
        errorContext: 'SBCs',
        downloadType: 'sbc',
      }),
    [downloadSelectedDocuments, selectedSbcExternalIds]
  );

  const downloadSelectedSubscriberEmails = React.useCallback(
    async (auditIds: number[]) => {
      if (isDownloading || auditIds.length === 0) {
        return;
      }

      const apiBase = getApiUrl();
      const endpoint = new URL('/document/subscriber-email/batch', apiBase).toString();

      try {
        setIsDownloading(true);
        setActiveDownload('subscriberEmail');
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ auditIds }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || 'Failed to download subscriber email.');
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'Summary of Benefits and Coverage for your plan.eml';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Failed to download subscriber email', error);
        window.alert('Unable to download the subscriber email. Please try again.');
      } finally {
        setIsDownloading(false);
        setActiveDownload(null);
      }
    },
    [isDownloading]
  );

  const handleDownloadSelectedSubscriberEmails = React.useCallback(
    () => downloadSelectedSubscriberEmails(selectedAuditIdsForEmail),
    [downloadSelectedSubscriberEmails, selectedAuditIdsForEmail]
  );

  if (isLoading) return <DistributionGridSkeleton />;

  return (
    <div>
      <Grid
        style={{ fontSize: '12px' }}
        data={distributionData}
        dataItemKey="id"
        pageable={{ buttonCount: 10, pageSizes: [10, 20, 50], pageSizeValue: 5 }}
        skip={dataState.skip}
        take={dataState.take}
        total={auditCount}
        sortable={true}
        sort={dataState.sort}
        onDataStateChange={handleDataStateChange}
        onSortChange={handleSortChange}
        selectable={{
          enabled: true,
          drag: false,
          cell: false,
          mode: 'multiple',
        }}
        select={select}
        onSelectionChange={onSelectionChange}
        onHeaderSelectionChange={onHeaderSelectionChange}
      >
        <Column columnType="checkbox" width="50px" />
        <Column field="status" title="Status" width="150px" />
        <Column field="controlName" title="Control Name" />
        <Column field="planName" title="Plan Name" />
        <Column field="requestId" title="Request ID" />
        <Column field="coveragePeriod" title="Coverage Period" />
        <Column title="" cells={{ data: DownloadSbcCell }} />
        <Column title="" cells={{ data: DownloadSubscriberEmailCell }} />
      </Grid>

      <div className="action-buttons" style={{ marginTop: '20px' }}>
        <button
          type="button"
          className="btn-download-sbcs"
          onClick={handleDownloadSelectedSBCs}
          disabled={sbcDownloadDisabled}
        >
          {isDownloading && activeDownload === 'sbc'
            ? 'Preparing Download...'
            : 'Download Selected SBCs'}
        </button>
        <button
          type="button"
          className="btn-download-emails"
          onClick={handleDownloadSelectedSubscriberEmails}
          disabled={subscriberEmailDownloadDisabled}
        >
          {isDownloading && activeDownload === 'subscriberEmail'
            ? 'Preparing Download...'
            : 'Download Subscriber Email'}
        </button>
      </div>
    </div>
  );
};

export default DistributionGrid;
